import { useEffect, useRef, useState } from "react";
import { get, isEmpty } from "lodash";
import * as Yup from "yup";
import DOMPurify from "dompurify";
import classNames from "classnames";
import { Formik, Form, Field } from "formik";
import { useSelector } from "react-redux";
import { selectSelectedNode, selectCollections, selectServices, selectSelectedServiceId } from "./index";
import { serviceRightPaneNodeIcon, deleteIcon } from "../../assets/icons";
import { renderServiceField } from "./services.helpers";
import NodeValidationAlert from "./NodeValidationAlert";
import { Alert } from "../../components/molecules";
import {
  API_RESPONSES,
  EMPTY_STRING,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  DATA_TYPES,
} from "../../constants";
import { FIELDS, ACE_EDITOR_FIELDS, UNSET } from "./datamodels.constants";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { VALIDATION_MESSAGES } from "../../constants";

const { UPDATE_SERVICE } = SPINNER_NAMES;
const {
  INPUTS,
  OUTPUTS,
  NODE_DESCRIPTION,
  MODEL_DEFINITION,
  OUTPUT_MODEL_TYPE,
  RETURN_VALUE_MODEL,
  CUSTOM,
  PREDEFINED,
  ERROR_MANAGEMENT,
  IF_FALSE,
  IF_TRUE,
  SMTP_CONNECTION,
  OUTPUT_LINK_POINTS,
  UPDATES,
  COMPUTATION,
  MODEL,
  KEY_VALUES,
  EXPRESSION,
  SQLEXPRESSION,
  APPLICATION_URLENCODED,
  UPDATE_TYPE,
  MULTI_COLLECTION,
  OUTPUT_FIELDS,
  STRIPE_WEBHOOK_ENABLED_EVENTS,
  STRIPE_WEBHOOK_URL,
  RAZORPAY_PAYMENT_METHODS,
  MAPPING_CONFIG,
} = FIELDS;
const { SUCCESS } = API_RESPONSES;
const { STRING, OBJECT } = DATA_TYPES;
const SUBFLOW = "subflow";
const startNodeFieldTypes = {
  body: "body",
  type: "type",
  single: "single",
  multi: "multi",
  custom: "custom",
};
const errorManagementHandle = {
  name: "errors",
  key: "errorManagement",
  description: "Errors occurred during node execution",
};
const groupByNodeFieldTypes = {
  groupBy: "groupby",
  fields: "fields",
  type: "type",
  expression: "expression",
};
const KEY_UPDATES = "keyUpdates";
const METADATA = "metadata";
const PAYMENT_METHOD_TYPES = "paymentMethodTypes";
const ALLOWED_COUNTRIES = "allowedCountries";
const computationField = {
  type: "type",
  expression: "expression",
  name: "name",
  sorting: "sorting",
};
const PARAMETER = "parameter";
const SAVED = "Saved";
const SERVICE_CONFIG = "serviceConfig";
const CONFIG = "config";
const MANUAL = "manual";
const NAME = "name";
const COUNT = "Count";
const QUERY_PARAMS = "queryParams";
const HEADERS = "headers";
const UPDATES_FIELD_NODEID = [14, 15];
const {
  SQL_EXPRESSION_REQUIRED,
  CHECK_SEMICOLON,
  CHECK_SQL_SEMICOLON,
  REQUIRED,
} = VALIDATION_MESSAGES;

const ServiceRightSidebar = ({
  setElements,
  nodes,
  edges,
  updateReactFlowStore,
  isSaved,
  removeHandle,
  updateSelectedNodeData,
  isViewOnlyMode,
  draggable,
}) => {
  const { data: allCollections } = useSelector(selectCollections);
  const allServices = useSelector(selectServices);
  const selectedServiceId = useSelector(selectSelectedServiceId);
  const selectedNode = useSelector(selectSelectedNode);

  const [currentNode, setCurrentNode] = useState(selectedNode);
  const [shouldShowCustomSection, setShouldShowCustomSection] = useState(true);
  const [isEmptySelectedNodeField, setIsEmptySelectedNodeField] = useState();
  const [selectedNodeFields, setSelectedNodeFields] = useState(EMPTY_ARRAY);
  const [selectedNodeConfig, setSelectedNodeConfig] = useState(EMPTY_OBJECT);
  const [inputLinks, setInputLinks] = useState(EMPTY_ARRAY);
  const [outputLinks, setOutputLinks] = useState(EMPTY_ARRAY);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const selectedNodeRef = useRef();

  const collections = allCollections.filter(({ isRuleTable }) => !isRuleTable);
  const subflowServices = allServices.filter(
    (service) => service.serviceType === SUBFLOW && service.id !== selectedServiceId
  );
  const ruleCollections = allCollections.filter(
    ({ isRuleTable }) => isRuleTable
  );

  useEffect(() => {
    if (selectedNode) setValues(selectedNode);
  }, [selectedNode, edges]);

  useEffect(() => {
    if (selectedNodeRef.current && updateSelectedNodeData)
      setValues(selectedNodeRef.current);
  }, [updateSelectedNodeData]);

  useEffect(() => {
    if (selectedNode) {
      const currentNode = nodes.find((node) => node.id === selectedNode.id);
      selectedNodeRef.current = currentNode?.data;
      if (currentNode) setCurrentNode(currentNode.data);
    }
  }, [selectedNode, nodes]);

  useEffect(() => {
    setShouldShowCustomSection(isEmptySelectedNodeField);
  }, [selectedNode, isEmptySelectedNodeField]);

  useEffect(() => {
    if (!currentNode) return;
    setSelectedNodeFields(
      get(currentNode, CONFIG, EMPTY_ARRAY).filter(({ enable }) => enable)
    );
  }, [setSelectedNodeFields, currentNode]);

  useEffect(() => {
    if (selectedNodeFields)
      setIsEmptySelectedNodeField(!isEmpty(selectedNodeFields));
  }, [selectedNodeFields]);

  useEffect(() => {
    if (!currentNode) return;
    setInputLinks(get(currentNode, "inputs", EMPTY_ARRAY));
    setOutputLinks(get(currentNode, "outputs", EMPTY_ARRAY));
  }, [currentNode]);

  const setValues = (node) => {
    if (!node) return;

    const initialValues = node.config?.reduce((obj, item) => {
      if (item.enable) obj[item.key] = item.defaultValue ?? EMPTY_STRING;
      return obj;
    }, {});
    setSelectedNodeConfig(initialValues);
  };

  const getValidationSchema = ({ name, key, required }) => {
    if (name === RETURN_VALUE_MODEL)
      return Yup.string().when(OUTPUT_MODEL_TYPE, {
        is: (val) => val === PREDEFINED,
        then: Yup.string().required(REQUIRED),
      });
    else if (key) {
      switch (key) {
        case SQLEXPRESSION:
          return Yup.string()
            .required(SQL_EXPRESSION_REQUIRED)
            .test(
              CHECK_SQL_SEMICOLON,
              CHECK_SEMICOLON,
              (value) => value && value.endsWith(";")
            );
        case MODEL_DEFINITION:
          return Yup.mixed().when(OUTPUT_MODEL_TYPE, {
            is: (val) => val === CUSTOM,
            then: Yup.mixed().test(
              'isArrayOrObject',
              'Field must be an array or an object',
              (value) => (Array.isArray(value) || typeof value === OBJECT)
            ),
          });
        case startNodeFieldTypes.body:
          return Yup.object({
            type: Yup.string().required(),
            modelName: Yup.string().when(startNodeFieldTypes.type, {
              is: (val) => val === startNodeFieldTypes.single,
              then: Yup.string().required(),
            }),
            count: Yup.string().when(startNodeFieldTypes.type, {
              is: (val) => val === startNodeFieldTypes.single,
              then: Yup.string().required(),
            }),
            models: Yup.array().when(startNodeFieldTypes.type, {
              is: (val) => val === startNodeFieldTypes.multi,
              then: Yup.array()
                .min(1)
                .of(
                  Yup.object({
                    count: Yup.string().required(),
                    id: Yup.string().required(),
                    modelName: Yup.string().required(),
                    name: Yup.string().required(),
                  })
                ),
            }),
            customModels: Yup.object().when(startNodeFieldTypes.type, {
              is: (val) => val === startNodeFieldTypes.custom,
              then: Yup.object({
                fields: Yup.array()
                  .min(1)
                  .of(
                    Yup.object({
                      id: Yup.string().required(),
                      name: Yup.string().required(),
                    })
                  ),
              }),
            }),
          });
        case UPDATES: {
          if (UPDATES_FIELD_NODEID.includes(currentNode.nodeId))
            return Yup.array()
              .of(
                Yup.object({
                  expression: Yup.string().when(UPDATE_TYPE, {
                    is: (val) => val !== UNSET,
                    then: Yup.string().required(),
                  }),
                })
              )
              .min(1)
              .required();
          else
            return Yup.array()
              .of(
                Yup.object({
                  expression: Yup.string().required(),
                })
              )
              .required();
        }
        case groupByNodeFieldTypes.groupBy:
          return Yup.object({
            type: Yup.string().required(),
            expression: Yup.string().when(groupByNodeFieldTypes.type, {
              is: (val) => val === groupByNodeFieldTypes.expression,
              then: Yup.string().required(),
            }),
            fields: Yup.array().when(groupByNodeFieldTypes.type, {
              is: (val) => val === groupByNodeFieldTypes.fields,
              then: Yup.array().min(1).required(),
            }),
          });
        case COMPUTATION:
          return Yup.array()
            .of(
              Yup.object().shape({
                name: Yup.string().required(),
                type: Yup.string().required(),
                sorting: Yup.string().required(),
                expression: Yup.string().when(computationField.type, {
                  is: (val) => val !== COUNT,
                  then: Yup.string().required(),
                }),
              })
            )
            .min(1)
            .required();
        case PARAMETER:
          return Yup.array().of(
            Yup.object({
              count: Yup.string().required(),
              id: Yup.string().required(),
              name: Yup.string().required(),
              type: Yup.string().required(),
            })
          );
        case MODEL:
          return Yup.array()
            .min(1)
            .of(
              Yup.object({
                name: Yup.string().trim().required(),
                type: Yup.string().required(),
              })
            );
        case KEY_VALUES: {
          if (required)
            return Yup.array()
              .min(1)
              .of(
                Yup.object({
                  key: Yup.string().trim().required(),
                  value: Yup.string().trim().required(),
                  id: Yup.string().required(),
                  name: Yup.string().trim().required(),
                  type: Yup.string().required(),
                })
              );
          else return Yup.array();
        }
        case STRIPE_WEBHOOK_URL:
          return Yup.string();
        case ALLOWED_COUNTRIES:
          return Yup.array().min(1).required();
        case SMTP_CONNECTION:
          return Yup.object().required();
        case PAYMENT_METHOD_TYPES:
        case KEY_UPDATES:
          return Yup.array().min(1).required();
        case RAZORPAY_PAYMENT_METHODS:
        case STRIPE_WEBHOOK_ENABLED_EVENTS:
        case METADATA:
          return Yup.array();
        case QUERY_PARAMS:
        case HEADERS:
        case APPLICATION_URLENCODED:
          return Yup.array().of(
            Yup.object({
              key: Yup.string().trim().required(),
              value: Yup.string().trim().required(),
            })
          );
        case IF_FALSE:
        case IF_TRUE:
          return Yup.array().min(1).required(REQUIRED);
        case MULTI_COLLECTION:
          return Yup.array()
            .min(1)
            .of(
              Yup.object({
                id: Yup.string().required(),
                value: Yup.string().trim().required(),
              })
            )
            .test("unique", "repetitive collection", (values) => {
              const selectedCollections = values.map(({ value }) => value);
              return (
                selectedCollections.length === new Set(selectedCollections).size
              );
            });
        case OUTPUT_FIELDS:
          return Yup.array().min(1);
        case MAPPING_CONFIG:
          return Yup.object({
            mapperObject: Yup.object().required(),
          });
        default:
          if (required) return Yup.string().trim().required();
          else return Yup.string().trim();
      }
    }
  };

  const validationObject = selectedNodeFields?.reduce((obj, item) => {
    obj[item.key] = getValidationSchema(item);
    if (!ACE_EDITOR_FIELDS.includes(item.key) && item.type === EXPRESSION)
      obj[item.key] = obj[item.key].test(
        "check-expression-field",
        `Invalid ${item.key} expression`,
        (values) => true
      );
    return obj;
  }, {});

  const handleNodeHandleChanges = (
    e,
    fieldValue,
    fieldType,
    handleKey,
    propertyName,
    nodesData
  ) => {
    let data = nodesData;
    switch (fieldType) {
      case OUTPUTS:
      case INPUTS: {
        const currentNodeHandles = get(currentNode, fieldType, EMPTY_ARRAY);
        const updatedCurrentNodeHandles = currentNodeHandles.map((handle) =>
          handle.key === handleKey
            ? { ...handle, [propertyName]: fieldValue }
            : handle
        );
        data = { ...nodesData, [fieldType]: updatedCurrentNodeHandles };
        break;
      }
      case ERROR_MANAGEMENT: {
        const currentNodeHandles = get(currentNode, OUTPUTS, EMPTY_ARRAY);
        let updatedCurrentNodeHandles = currentNodeHandles;
        if (e.target.value !== MANUAL)
          updatedCurrentNodeHandles = currentNodeHandles.filter(
            (handle) => handle.key !== fieldType
          );
        else {
          const errorHandle = currentNodeHandles.find(
            (handle) => handle.key === fieldType
          );
          if (!errorHandle)
            updatedCurrentNodeHandles = [
              ...currentNodeHandles,
              errorManagementHandle,
            ];
        }
        data = {
          ...nodesData,
          [OUTPUTS]: updatedCurrentNodeHandles,
        };
        break;
      }
      case IF_FALSE:
      case IF_TRUE: {
        const outputHandles = get(currentNode, OUTPUTS, EMPTY_ARRAY);
        // TBD: Removed to so that logical node's inputs selection can work again.
        // if (fieldValue.includes(EMPTY_STRING)) break;
        if (handleKey) {
          const newHandler = {
            name: handleKey,
            custom: true,
            key: fieldValue,
            type: "object",
          };

          data = { ...nodesData, [OUTPUTS]: [...outputHandles, newHandler] };
        } else {
          removeHandle({
            nodeId: currentNode.id,
            handleId: fieldValue,
            type: OUTPUTS,
            shouldSetElements: true,
          });
          data = null;
        }
        break;
      }
      case NODE_DESCRIPTION:
        data = { ...nodesData, [fieldType]: fieldValue };
        break;
      default:
    }
    return data;
  };

  const handleOnChange = (
    handleChange,
    setFieldValue,
    e,
    fieldType = EMPTY_STRING,
    handleKey = EMPTY_STRING,
    propertyName = NAME,
    updateField = {}
  ) => {
    let fieldValue = e;
    if (e.target) {
      handleChange(e);
      fieldValue = e.target.value;
    } else setFieldValue(fieldType, e);

    const currentSelectedNode = nodes.find(
      (node) => node.id === selectedNode.id
    );
    const updatedCurrentNodeConfig = currentSelectedNode.data.config.map(
      (field) => {
        if (field.key === fieldType)
          field = { ...field, defaultValue: fieldValue };
        if (updateField.enable?.includes(field.key))
          field = { ...field, enable: true };
        if (updateField.disable?.includes(field.key))
          field = { ...field, enable: false };
        return field;
      }
    );

    let data = {
      ...currentNode,
      [CONFIG]: updatedCurrentNodeConfig,
    };

    if (fieldType)
      data = handleNodeHandleChanges(
        e,
        fieldValue,
        fieldType,
        handleKey,
        propertyName,
        data
      );

    if (data) {
      const updatedNodes = nodes.map((node) => {
        if (node.id === currentNode.id) return { ...node, data };
        return node;
      });
      setCurrentNode(data);
      setElements({ nodes: updatedNodes });
      if (updateField.enable || updateField.disable) setValues(data);
    }
  };

  const handleOnBlur = (
    handleChange,
    setFieldValue,
    e,
    fieldType,
    handleKey,
    index
  ) => {
    if (!e.target.value) {
      e = { ...e, target: { ...e.target, value: String(fieldType + index) } };
      handleOnChange(handleChange, setFieldValue, e, fieldType, handleKey);
    }
  };

  const getReactFlowStoreNodes = (nodes) =>
    nodes.map((node) => ({ ...node, selected: false }));

  const handleOnSubmit = (values) => {
    for (let key in values) {
      if (typeof values[key] === STRING) {
        values[key] = values[key].trim();
      }
    }
    const updatedNodes = nodes.map((node) => {
      if (node.id === currentNode.id)
        return {
          ...node,
          data: {
            ...node.data,
            [SERVICE_CONFIG]: values,
            isValidated: true,
          },
        };
      return node;
    });
    setElements({ nodes: updatedNodes });
    updateReactFlowStore(getReactFlowStoreNodes(updatedNodes));
  };

  const shouldShowOutputLinks = () =>
    !isEmpty(outputLinks) &&
    !selectedNodeFields.find((field) => field.key === OUTPUT_LINK_POINTS);

  const shouldRenderWebHookField = (field) => !field.parentKey && field.name;

  return (
    <Formik
      enableReinitialize
      key={selectedNode?.id}
      initialValues={selectedNodeConfig}
      validationSchema={Yup.object(validationObject)}
      onSubmit={handleOnSubmit}
    >
      {({ values, handleChange, touched, errors, setFieldValue, isValid }) => (
        <Form className="w-full h-full">
          {selectedNode ? (
            <>
              <div className="flex flex-col h-[calc(100%-40px)]">
                <div className="bg-gray-800 sticky top-0 py-px z-10">
                  {isSaved ? (
                    <div className="p-2">
                      <Alert
                        type={SUCCESS}
                        message={SAVED}
                        timeout={EMPTY_STRING}
                        shouldShowAlertType={false}
                      />
                    </div>
                  ) : (
                    <NodeValidationAlert
                      requiredFields={selectedNodeFields}
                      errors={errors}
                    />
                  )}
                </div>
                <div className="text-center border border-gray-200 rounded p-1 mx-2">
                  <span className="text-base text-gray-50">
                    {selectedNode.name}
                  </span>
                </div>
                <div className="border-b border-gray-200">
                  <ul className="flex flex-wrap text-sm font-medium text-gray-500 mx-3 mt-1">
                    {!isEmpty(selectedNodeFields) && (
                      <li className="w-2/4">
                        <button
                          type="button"
                          className={classNames(
                            "inline-block py-2 border-b-4 rounded-t-lg border-transparent w-full",
                            {
                              "border-blue-200 text-gray-50":
                                shouldShowCustomSection,
                              "cursor-col-resize": draggable,
                            }
                          )}
                          onClick={() => setShouldShowCustomSection(true)}
                        >
                          Custom
                        </button>
                      </li>
                    )}
                    <li className="w-2/4">
                      <button
                        type="button"
                        className={classNames(
                          "inline-block py-2 border-b-4 rounded-t-lg border-transparent ease-in w-full",
                          {
                            "border-blue-200 text-gray-50":
                              !shouldShowCustomSection,
                            "cursor-col-resize": draggable,
                          }
                        )}
                        onClick={() => setShouldShowCustomSection(false)}
                      >
                        Content
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="overflow-y-scroll no-scrollbar grow">
                  {shouldShowCustomSection ? (
                    <>
                      {selectedNodeFields.map(
                        (each) =>
                          shouldRenderWebHookField(each) && (
                            <div
                              className="form-group text-gray-50 border-b border-gray-700 p-4"
                              key={each.key}
                            >
                              <fieldset disabled={isViewOnlyMode}>
                                <div className="flex items-center">
                                  <span className="text-base">{each.name}</span>
                                  <span
                                    className={classNames(
                                      "ml-2 text-red-600 text-xl mt-1",
                                      {
                                        hidden: !each.required,
                                      }
                                    )}
                                  >
                                    *
                                  </span>
                                </div>
                                <p
                                  dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                      each.description?.replaceAll("bold", "b")
                                    ),
                                  }}
                                  className="text-[10px] my-1"
                                />
                                {renderServiceField({
                                  each,
                                  values,
                                  handleChange: (
                                    event,
                                    fieldtype,
                                    handleKey,
                                    propertyName,
                                    updateField
                                  ) =>
                                    handleOnChange(
                                      handleChange,
                                      setFieldValue,
                                      event,
                                      fieldtype,
                                      handleKey,
                                      propertyName,
                                      updateField
                                    ),
                                  nodes,
                                  edges,
                                  setElements,
                                  collections,
                                  ruleCollections,
                                  subflowServices,
                                  setFieldValue,
                                  currentNode,
                                  inputLinks,
                                  outputLinks,
                                  removeHandle,
                                  draggable,
                                })}
                                {touched[each.key] && errors[each.key] && (
                                  <div className="text-xs p-2 my-1 rounded-md border-2 border-red-500 text-red-600 text-center">
                                    {errors[each.key] instanceof Object
                                      ? "All fields are required."
                                      : errors[each.key]}
                                  </div>
                                )}
                              </fieldset>
                            </div>
                          )
                      )}
                    </>
                  ) : (
                    <div className="form-group" key="description">
                      <div className="border-b border-gray-700 p-4">
                        <label className="text-base font-medium text-white mb-1 block">
                          Description
                        </label>
                        <p className="text-[10px] text-gray-50 italic mb-2">
                          Short description of what this node does
                        </p>
                        <Field
                          as="textarea"
                          className="form-control scrollbar-light"
                          name={NODE_DESCRIPTION}
                          value={get(
                            currentNode,
                            "nodeDescription",
                            EMPTY_STRING
                          )}
                          placeholder="description"
                          onChange={(e) => {
                            handleOnChange(
                              handleChange,
                              setFieldValue,
                              e,
                              NODE_DESCRIPTION
                            );
                          }}
                          disabled={isViewOnlyMode}
                        />
                      </div>
                      {!!inputLinks.length && (
                        <div className="border-b border-gray-700 p-4">
                          <label className="text-base font-medium text-gray-50 block">
                            Input link points
                            {inputLinks.map((eachInput, index) => {
                              return (
                                <fieldset
                                  disabled={isViewOnlyMode}
                                  key={eachInput.key}
                                  className="flex flex-row my-1"
                                >
                                  <Field
                                    key={eachInput.key}
                                    className="form-control"
                                    type="text"
                                    name={eachInput.key}
                                    value={eachInput.name}
                                    placeholder={eachInput.key}
                                    onChange={(e) =>
                                      handleOnChange(
                                        handleChange,
                                        setFieldValue,
                                        e,
                                        INPUTS,
                                        eachInput.key
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleOnBlur(
                                        handleChange,
                                        setFieldValue,
                                        e,
                                        INPUTS,
                                        eachInput.key,
                                        index
                                      )
                                    }
                                  />
                                  {eachInput.custom && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeHandle({
                                          nodeId: currentNode.id,
                                          handleId: eachInput.key,
                                          type: INPUTS,
                                          shouldSetElements: true,
                                        })
                                      }
                                      className="cursor-pointer disabled:cursor-not-allowed"
                                    >
                                      <img
                                        src={deleteIcon}
                                        width="20"
                                        alt="delete"
                                      />
                                    </button>
                                  )}
                                </fieldset>
                              );
                            })}
                          </label>
                        </div>
                      )}
                      {shouldShowOutputLinks() && (
                        <div className="border-b border-gray-700 p-4">
                          <label className="text-base font-medium text-gray-50 block">
                            Output link points
                            {outputLinks.map((eachOutput, index) => {
                              return (
                                <fieldset
                                  disabled={isViewOnlyMode}
                                  className="flex gap-1 mt-1 flex-wrap text-sm"
                                  key={eachOutput.key}
                                >
                                  <Field
                                    key={eachOutput.key}
                                    className="form-control"
                                    type="text"
                                    name={eachOutput.key}
                                    value={eachOutput.name}
                                    placeholder={eachOutput.key}
                                    onChange={(e) =>
                                      handleOnChange(
                                        handleChange,
                                        setFieldValue,
                                        e,
                                        OUTPUTS,
                                        eachOutput.key
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleOnBlur(
                                        handleChange,
                                        setFieldValue,
                                        e,
                                        OUTPUTS,
                                        eachOutput.key,
                                        index
                                      )
                                    }
                                  />
                                  {eachOutput.custom && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeHandle({
                                          nodeId: currentNode.id,
                                          handleId: eachOutput.key,
                                          type: OUTPUTS,
                                          shouldSetElements: true,
                                        })
                                      }
                                      className="cursor-pointer disabled:cursor-not-allowed"
                                    >
                                      <img
                                        src={deleteIcon}
                                        width="20"
                                        alt="delete"
                                      />
                                    </button>
                                  )}
                                </fieldset>
                              );
                            })}
                          </label>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="p-2 text-right mt-auto bg-gray-800 sticky -bottom-1">
                <button
                  className="btn btn-primary w-full"
                  disabled={isSpinnerActive || !isValid || isViewOnlyMode}
                  type="submit"
                >
                  <Spinner
                    name={UPDATE_SERVICE}
                    setIsSpinnerActive={setIsSpinnerActive}
                    persistSize
                  >
                    Save
                  </Spinner>
                </button>
              </div>
            </>
          ) : (
            <div className="icon flex flex-col justify-center items-center text-center mx-6 h-full">
              <div className="bg-gray-200 text-center justify-center h-32 w-32 lg:w-40 lg:h-40 rounded-full flex items-center">
                <img src={serviceRightPaneNodeIcon} width="100" alt="flow" />
              </div>
              <div className="text-center text-2xl mt-8 text-slate-300">
                No nodes selected
              </div>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default ServiceRightSidebar;
